/**
 * The "?" that explains a label without spending a line on it.
 *
 * A `button` rather than a `span` with a title, so it is reachable by keyboard
 * and announced. The bubble is a `FloatingTip`, positioned in script so it
 * stays inside the content column; the text is also repeated in an
 * `aria-describedby` target so it reaches a screen reader whatever the bubble
 * does. Focus opens it, which is what a tap on a touch screen produces.
 *
 * @module components/ui/HelpTip
 */

import React, { useId } from "react";

import { FloatingTip } from "./FloatingTip";

/** Where the bubble sits. A side would cover the field the icon labels. */
export const HELP_TIP_PLACEMENTS = ["top", "bottom"] as const;
export type HelpTipPlacement = (typeof HELP_TIP_PLACEMENTS)[number];

export interface HelpTipProps {
  /** The explanation. One sentence. */
  text: string;
  /** What it explains, for the accessible name: "About credit volume". */
  label: string;
  /** Above or below the icon. */
  placement?: HelpTipPlacement;
  /**
   * An element already carrying this text, for a caller that renders it for
   * its own control. Without it the tip renders its own copy, and a caller
   * that has one too leaves the same sentence in the DOM twice.
   */
  describedBy?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <HelpTip label="About ECTS" text="Credit volume for the whole programme." />
 * ```
 */
export function HelpTip({
  text,
  label,
  placement = "top",
  describedBy = undefined,
  className = "",
  "data-testid": testId,
}: HelpTipProps): React.ReactElement {
  const ownId = useId();
  const target = describedBy ?? ownId;
  return (
    <span className={`inline-flex shrink-0 ${className}`.trim()}>
      <FloatingTip tip={text} placement={placement} tone="soft">
        {({ "aria-describedby": _bubbleId, ...trigger }) => (
          // The control is 24px and the disc inside it is 18px. WCAG 2.2's
          // target-size floor is 24px, and an 18px button failed it on every
          // field that has help. The ring is what the reader sees; the padding
          // around it is what they can hit.
          <button
            type="button"
            aria-label={label}
            aria-describedby={target}
            data-testid={testId}
            className="inline-flex size-6 shrink-0 cursor-help items-center justify-center"
            {...trigger}
          >
            <span
              aria-hidden
              className="inline-flex size-[18px] items-center justify-center rounded-full border border-border-strong text-xs font-semibold tracking-normal text-muted normal-case"
            >
              ?
            </span>
          </button>
        )}
      </FloatingTip>
      {describedBy === undefined && (
        <span id={ownId} className="sr-only">
          {text}
        </span>
      )}
    </span>
  );
}
